// ==================================================================================
// Digital Approval System (DAS) - Workflow Templates Hook
// ==================================================================================
// Custom hook for workflow template operations
// ==================================================================================

import { useState, useEffect, useCallback } from 'react';
import { workflowTemplatesService } from '../../../api/services/das/workflowTemplatesService';
import type {
  WorkflowTemplateDto,
  WorkflowTemplateQueryParameters,
} from '../../../api/types/das/workflowTemplates.types';

/**
 * Hook for fetching and managing workflow templates
 */
export function useWorkflowTemplates(params?: WorkflowTemplateQueryParameters) {
  const [templates, setTemplates] = useState<WorkflowTemplateDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await workflowTemplatesService.getAll(params);
      setTemplates(data);
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to fetch workflow templates');
      console.error('Failed to fetch workflow templates:', err);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  return {
    templates,
    loading,
    error,
    refetch: fetchTemplates,
  };
}

/**
 * Hook for fetching active workflow templates only
 */
export function useActiveWorkflowTemplates() {
  const [templates, setTemplates] = useState<WorkflowTemplateDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await workflowTemplatesService.getActive();
      setTemplates(data);
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to fetch active workflow templates');
      console.error('Failed to fetch active workflow templates:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  return {
    templates,
    loading,
    error,
    refetch: fetchTemplates,
  };
}

/**
 * Hook for fetching workflow templates by category
 */
export function useWorkflowTemplatesByCategory(category: string | null) {
  const [templates, setTemplates] = useState<WorkflowTemplateDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTemplates = useCallback(async () => {
    if (!category) {
      setTemplates([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await workflowTemplatesService.getByCategory(category);
      setTemplates(data);
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to fetch workflow templates by category');
      console.error('Failed to fetch workflow templates by category:', err);
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  return {
    templates,
    loading,
    error,
    refetch: fetchTemplates,
  };
}
